// onboarding-view.ts — Willkommens-Onboarding für neue Registrierungen.
//
// Bei der ERSTEN Anmeldung (noch kein Vorname im Profil) begrüßt der Bär den
// User per Stimme und leitet ihn in 7 einfachen Schritten durch die Basis-
// Informationen. Format: VOICE + SICHTBARER FORTSCHRITT — der Bär SPRICHT jede
// Frage (TTS), die Antworten erscheinen in sichtbaren, korrigierbaren Feldern,
// pro Schritt ein Häkchen. Die Antworten wandern direkt ins Profil
// (PUT /api/user/profile); am Ende steht die erste echte Aufnahme als Aha-Moment.
//
// Frontend-getrieben & sicher: KEIN Eingriff in den Realtime-Agent. Die Stimme
// ist nur Ausgabe (TTS), die Eingabe bleibt sichtbar/tippbar → funktioniert
// überall, auch in der nativen Hülle.

import { sanft } from "./ruckel-schalter.js";
import {
  loadSettings,
  saveSettings,
  VOICE_OPTIONS,
} from "./settings.js";
import { buildSheetHeader } from "./sheet-ui.js";
import { zeichen } from "./zeichen.js";
import { ZUHAUSE_WEGE } from "./zuhause.js";
import {
  baueGeburtslandAuswahl,
  geburtslandAnzeige,
} from "./geburtsland-auswahl.js";
import { geburtslandFinden } from "../../shared/geburtsland.js";

const DONE_KEY = "onboarding:done";

// --------------------------------------------------------------------------
// Bär spricht (TTS) — repliziert prepareTtsUrl, spielt direkt ab.
// --------------------------------------------------------------------------
let currentAudio: HTMLAudioElement | null = null;
async function speak(text: string, awaitEnd = false): Promise<void> {
  try {
    currentAudio?.pause();
    const voiceId = loadSettings().voiceId;
    const r = await fetch("/api/tts/prepare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ text, voiceId }),
    });
    const j = (await r.json().catch(() => null)) as {
      ok?: boolean;
      token?: string;
    } | null;
    if (!r.ok || !j?.ok || !j.token) return;
    const a = new Audio(`/api/tts/stream?token=${encodeURIComponent(j.token)}`);
    currentAudio = a;
    if (awaitEnd) {
      // Freihändig: warten, bis der Bär fertig gesprochen hat, BEVOR wir
      // zuhören — sonst nehmen wir seine eigene Stimme mit auf.
      await new Promise<void>((resolve) => {
        a.addEventListener("ended", () => resolve(), { once: true });
        a.addEventListener("error", () => resolve(), { once: true });
        void a.play().catch(() => resolve());
      });
    } else {
      await a.play().catch(() => {
        /* Autoplay evtl. blockiert — kein Drama, Text steht ja da */
      });
    }
  } catch {
    /* TTS ist Beiwerk; das Onboarding läuft auch stumm weiter */
  }
}

// --------------------------------------------------------------------------
// Freihändig: Mikrofon offen halten + Stille-Erkennung (VAD)
// --------------------------------------------------------------------------
let micStream: MediaStream | null = null;
let micAnalyser: AnalyserNode | null = null;
let micBuf: Uint8Array<ArrayBuffer> | null = null;
let micCtx: AudioContext | null = null;

// Im User-Tap (Stimmen-Wahl) aufrufen — schaltet auf iOS Mikro + Audio frei.
async function acquireMic(): Promise<boolean> {
  try {
    if (micStream && micAnalyser) return true;
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    micCtx = new Ctx();
    await micCtx.resume().catch(() => {});
    const src = micCtx.createMediaStreamSource(micStream);
    micAnalyser = micCtx.createAnalyser();
    micAnalyser.fftSize = 512;
    src.connect(micAnalyser);
    micBuf = new Uint8Array(micAnalyser.fftSize);
    return true;
  } catch {
    micStream = null;
    micAnalyser = null;
    return false;
  }
}

function releaseMic(): void {
  try {
    micStream?.getTracks().forEach((t) => t.stop());
    void micCtx?.close();
  } catch {
    /* egal */
  }
  micStream = null;
  micAnalyser = null;
  micBuf = null;
  micCtx = null;
}

// Nimmt auf, bis der Nutzer ~1,4 s still ist (oder Limits), dann Transkript.
function listenVAD(): Promise<string> {
  if (!micStream || !micAnalyser || !micBuf) return Promise.resolve("");
  const analyser = micAnalyser;
  const buf = micBuf;
  const mr = new MediaRecorder(micStream);
  const chunks: Blob[] = [];
  mr.addEventListener("dataavailable", (e) => {
    const b = (e).data;
    if (b && b.size) chunks.push(b);
  });
  mr.start();
  const t0 = Date.now();
  let lastLoud = 0;
  let heard = false;
  const THRESH = 0.018;
  const SILENCE = 1400;
  const NOSPEECH = 8000;
  const MAXMS = 25000;
  return new Promise<string>((resolve) => {
    let done = false;
    const iv = window.setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms > THRESH) {
        heard = true;
        lastLoud = now;
      }
      const silentDone = heard && now - lastLoud > SILENCE;
      const nothingDone = !heard && now - t0 > NOSPEECH;
      if (silentDone || nothingDone || now - t0 > MAXMS) {
        if (done) return;
        done = true;
        window.clearInterval(iv);
        mr.addEventListener("stop", () => {
          if (!heard) {
            resolve("");
            return;
          }
          const type = mr.mimeType || "audio/webm";
          const ext = /mp4|mpeg|m4a/.test(type)
            ? "mp4"
            : /ogg/.test(type)
              ? "ogg"
              : "webm";
          const fd = new FormData();
          fd.append("audio", new Blob(chunks, { type }), `antwort.${ext}`);
          fetch("/api/transcribe", {
            method: "POST",
            body: fd,
            credentials: "same-origin",
          })
            .then((r) => r.json())
            .then((j) => resolve(((j?.text) || "").trim()))
            .catch(() => resolve(""));
        });
        try {
          mr.stop();
        } catch {
          resolve("");
        }
      }
    }, 90);
  });
}

function stopSpeaking(): void {
  try {
    currentAudio?.pause();
  } catch {
    /* egal */
  }
  currentAudio = null;
}

// --------------------------------------------------------------------------
// Profil laden / First-Run-Erkennung
// --------------------------------------------------------------------------
interface Profile {
  firstName?: string | null;
  birthday?: string | null;
  birthTime?: string | null;
  birthPlace?: string | null;
  /** 24.09.2026, Ticket 2567: Geburtsland-Schlüssel (Zeitzone des Charts). */
  birthCountry?: string | null;
}

async function fetchProfile(): Promise<Profile | null> {
  try {
    const r = await fetch("/api/user/profile", { credentials: "same-origin" });
    if (!r.ok) return null;
    // ⚠️ 21.09.2026: Die Route antwortet `{ profile: {...} }`. Hier wurde die
    //    HUELLE als Profil gelesen — firstName war immer undefined, und jeder
    //    Nutzer bekam in jedem neuen Browser das Willkommen erneut (Albert,
    //    Firefox, mit vollstaendigem Profil). Martin: „Kann es sein, dass
    //    Albert sich noch gar nicht im Browser eingeloggt hat?" — doch, hatte er.
    const roh = (await r.json()) as { profile?: Profile } & Profile;
    return roh.profile ?? roh;
  } catch {
    return null;
  }
}

/**
 * Nach dem Login aufrufen. Zeigt das Onboarding nur, wenn noch kein Vorname im
 * Profil steht UND es auf diesem Gerät nicht schon abgeschlossen/übersprungen
 * wurde. Fail-safe: bei jedem Fehler passiert nichts (kein Block der App).
 */
export async function maybeStartOnboarding(): Promise<void> {
  try {
    if (localStorage.getItem(DONE_KEY)) return;
  } catch {
    /* localStorage evtl. blockiert → trotzdem prüfen */
  }
  const profile = await fetchProfile();
  if (profile && (profile.firstName ?? "").trim()) return; // schon onboarded
  openOnboarding(profile ?? {});
}

function markDone(): void {
  try {
    localStorage.setItem(DONE_KEY, new Date().toISOString());
  } catch {
    /* egal */
  }
}

// --------------------------------------------------------------------------
// Schritte
// --------------------------------------------------------------------------
interface Answers {
  firstName: string;
  anrede: "Du" | "Sie" | "";
  birthday: string;
  birthTime: string;
  birthPlace: string;
  /** Schlüssel aus shared/geburtsland.ts; "" = nicht gewählt (Österreich). */
  birthCountry: string;
  anliegen: string;
  beruf: string;
  uebermich: string;
  abendZeit: string;
  anonym: "anonym" | "vorname" | "";
}

const ans: Answers = {
  firstName: "",
  anrede: "",
  birthday: "",
  birthTime: "",
  birthPlace: "",
  birthCountry: "",
  anliegen: "",
  beruf: "",
  uebermich: "",
  abendZeit: "",
  anonym: "",
};

interface Step {
  key: string;
  title: string;
  /** Vom Bär gesprochene + angezeigte Frage. */
  question: () => string;
  /** Felder rendern; ruft `valid(bool)` bei jeder Änderung. */
  render: (host: HTMLElement, valid: (ok: boolean) => void) => void;
  /** Schritt gültig abgeschlossen? */
  isValid: () => boolean;
  /** Kompakte Antwort-Zusammenfassung für den erledigten Zustand. */
  summary: () => string;
  /** Darf übersprungen werden? */
  optional?: boolean;
  /** Gesprochene Antwort (Transkript) auf die Felder anwenden (best effort). */
  applyVoice?: (text: string) => void;
  /**
   * WIE wird dieser Schritt beantwortet?
   *
   * ⚠️ 15.09.2026, Martin nach dem eigenen Durchlauf: *„Ich habe gesagt, per
   *    du, dann hat er gesagt, das habe ich nicht ganz. Sag nochmal. Also das
   *    Ganze ist Bullshit. In Wahrheit kann man Dinge, die in der Auswahl
   *    sind, wie per Du oder per Sie, einfach mit den Fingern machen. Und
   *    wenn mehr Text ist, kann man es sprechen."*
   *
   *    Er hat recht, und die Ursache ist nachlesbar: die Erkennung lieferte
   *    „Perdoo" — ein Wort. Das Muster suchte `\bdu\b`, also ein eigenes
   *    Wort, und fand nichts. Eine Auswahl aus zwei Knöpfen über Spracherkennung
   *    zu führen, setzt eine ganze Fehlerquelle auf eine Frage, die ein
   *    Fingertipp fehlerfrei beantwortet.
   *
   *  - `"wahl"` — feste Möglichkeiten. Der Bär FRAGT, danach entscheidet der
   *    Finger. Kein Zuhören, kein „Verstanden: …", kein Nachfragen. Der Tipp
   *    IST die Antwort, also geht es danach von selbst weiter.
   *  - `"feld"` — Datum, Uhrzeit und Ähnliches. Auch Finger, aber der Tipp
   *    allein ist noch keine fertige Antwort — hier bleibt „Weiter".
   *  - `"text"` — freie Antwort. Hier darf gesprochen werden, dafür ist es da.
   */
  eingabe: "wahl" | "feld" | "text";
  /**
   * Gehört dieser Schritt in den ERSTEN Durchlauf?
   *
   * ⚠️ Martin: *„Das kann man einfach vereinfachen."* Neun Fragen, bevor man
   *    das erste Wort mit Vespera gewechselt hat, sind zu viele. Was hier auf
   *    `false` steht, ist nicht gelöscht — es wird im Gespräch ohnehin
   *    erfragt, wo es hingehört.
   */
  imErstlauf?: boolean;
}

// --- Sprach-Antwort: Audio aufnehmen → /api/transcribe → Text -------------
async function startRecording(): Promise<{ stop: () => Promise<string> }> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mr = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  mr.addEventListener("dataavailable", (e) => {
    const b = (e).data;
    if (b && b.size) chunks.push(b);
  });
  mr.start();
  return {
    stop: () =>
      new Promise<string>((resolve) => {
        mr.addEventListener("stop", () => {
          stream.getTracks().forEach((t) => t.stop());
          const type = mr.mimeType || "audio/webm";
          const ext = /mp4|mpeg|m4a/.test(type)
            ? "mp4"
            : /ogg/.test(type)
              ? "ogg"
              : "webm";
          const fd = new FormData();
          fd.append("audio", new Blob(chunks, { type }), `antwort.${ext}`);
          fetch("/api/transcribe", {
            method: "POST",
            body: fd,
            credentials: "same-origin",
          })
            .then((r) => r.json())
            .then((j) => resolve(((j?.text) || "").trim()))
            .catch(() => resolve(""));
        });
        mr.stop();
      }),
  };
}

function cleanName(t: string): string {
  let s = t.trim().replace(/[.?!,]+$/g, "");
  s = s.replace(
    /^(ich\s+(heisse|heiße|bin)|mein\s+name\s+ist|man\s+nennt\s+mich|nenn(e)?\s+mich)\s+/i,
    "",
  );
  const first = (s.split(/\s+/).filter(Boolean)[0] || "").replace(
    /[^A-Za-zÄÖÜäöüß-]/g,
    "",
  );
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "";
}
function parseTime(t: string): string {
  let m = t.match(/(\d{1,2})\s*[:.]\s*(\d{2})/);
  if (m && Number(m[1]) <= 23 && Number(m[2]) <= 59)
    return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = t.match(/(\d{1,2})\s*uhr(?:\s*(\d{1,2}))?/i);
  if (m && Number(m[1]) <= 23 && Number(m[2] || "0") <= 59)
    return `${m[1].padStart(2, "0")}:${(m[2] || "0").padStart(2, "0")}`;
  return "";
}
const MONTHS: Record<string, string> = {
  januar: "01",
  februar: "02",
  märz: "03",
  maerz: "03",
  april: "04",
  mai: "05",
  juni: "06",
  juli: "07",
  august: "08",
  september: "09",
  oktober: "10",
  november: "11",
  dezember: "12",
};
function fourDigitYear(y: string): string {
  if (y.length === 4) return y;
  const n = Number.parseInt(y, 10);
  const pivot = new Date().getFullYear() % 100;
  return String(n <= pivot ? 2000 + n : 1900 + n);
}
function fmtDateDE(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const months = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  return `${Number(m[3])}. ${months[Number(m[2]) - 1]} ${m[1]}`;
}
function parseDate(t: string): string {
  // TT<Trenner>MM<Trenner>JJJJ|JJ — Trenner: Punkt, Komma, Schrägstrich, Bindestrich oder Leerzeichen.
  let m = t.match(
    /\b(\d{1,2})\s*[.,\-/ ]\s*(\d{1,2})\s*[.,\-/ ]\s*(\d{2,4})\b/,
  );
  if (m)
    return `${fourDigitYear(m[3])}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // TT. Monatsname JJJJ|JJ
  m = t.match(
    /\b(\d{1,2})\.?\s*(januar|februar|märz|maerz|april|mai|juni|juli|august|september|oktober|november|dezember)\s*(\d{2,4})\b/i,
  );
  if (m) {
    const mo = MONTHS[m[2].toLowerCase()];
    if (mo) return `${fourDigitYear(m[3])}-${mo}-${m[1].padStart(2, "0")}`;
  }
  return "";
}

function du(): string {
  return ans.anrede === "Sie" ? "Sie" : "du";
}

/** „dein" oder „Ihr" — das Besitzwort zur gewählten Anrede. */
function dein(): string {
  return ans.anrede === "Sie" ? "Ihr" : "dein";
}

/**
 * Zwei Fassungen desselben Satzes — eine fürs Du, eine fürs Sie.
 *
 * ⚠️ 15.09.2026: Es reicht NICHT, das Fürwort zu tauschen. Das Zeitwort
 *    ändert sich mit („erzähl" gegen „erzählen Sie"), das Besitzwort auch.
 *    Wer hier nur `du()` einsetzt, baut Sätze, die niemand sagt.
 *
 * ⚠️ Gefunden, weil vier Texte am Ende des Ablaufs das Du fest eingetippt
 *    hatten. Wer „Per Sie" gewählt hatte, wurde ab dem vorletzten Schritt
 *    wieder geduzt — und zwar im Abschluss, den jeder sieht.
 */
function siez(duFassung: string, sieFassung: string): string {
  return ans.anrede === "Sie" ? sieFassung : duFassung;
}

function input(
  type: string,
  placeholder: string,
  value: string,
  on: (v: string) => void,
): HTMLInputElement {
  const el = document.createElement("input");
  el.type = type;
  el.className = "onb-input";
  el.placeholder = placeholder;
  el.value = value;
  el.addEventListener("input", () => on(el.value));
  return el;
}

function choice(
  options: Array<{ label: string; value: string }>,
  current: () => string,
  on: (v: string) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "onb-choice";
  for (const o of options) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "onb-choice-btn";
    b.textContent = o.label;
    const sync = (): void => {
      b.classList.toggle("is-active", current() === o.value);
    };
    b.addEventListener("click", () => {
      on(o.value);
      wrap
        .querySelectorAll(".onb-choice-btn")
        .forEach((x) => x.classList.remove("is-active"));
      b.classList.add("is-active");
    });
    sync();
    wrap.appendChild(b);
  }
  return wrap;
}

// Was der Bär nach einer Sprach-Antwort zurück-bestätigt (organischer Fluss).
function confirmText(key: string): string {
  switch (key) {
    case "name":
      return `Schön, ${ans.firstName}.`;
    case "anrede":
      return ans.anrede === "Sie"
        ? "Gut, wir bleiben beim Sie."
        : "Super, dann duzen wir uns.";
    case "geburt": {
      const parts: string[] = [];
      if (ans.birthday) parts.push("am " + fmtDateDE(ans.birthday));
      if (ans.birthTime)
        parts.push("um " + ans.birthTime.replace(":", " Uhr "));
      if (ans.birthPlace) parts.push("in " + ans.birthPlace);
      return parts.length
        ? "Hab ich notiert: " + parts.join(", ") + ". Passt das?"
        : "Verstanden.";
    }
    case "anliegen":
      return "Danke, das hab ich.";
    case "abend":
      return ans.abendZeit
        ? "Notiert, " + ans.abendZeit.replace(":", " Uhr ") + "."
        : "Alles klar.";
    case "anonym":
      return ans.anonym === "vorname"
        ? "Mit Vornamen, nur für dich sichtbar."
        : "Andere bleiben anonym.";
    case "beruf":
      return ans.beruf ? "Schön, das merke ich mir." : "Alles klar.";
    case "uebermich":
      return ans.uebermich ? "Danke, das ist wertvoll." : "Alles gut.";
    default:
      return "Verstanden.";
  }
}

/**
 * Alle Schritte, die es gibt.
 *
 * ⚠️ Was hier steht, läuft nicht automatisch im ERSTEN Durchgang — dafür
 *    entscheidet `imErstlauf`. Siehe {@link erstlaufSchritte}.
 */
const STEPS: Step[] = [
  {
    key: "name",
    eingabe: "text",
    applyVoice: (t) => {
      const n = cleanName(t);
      if (n) ans.firstName = n;
    },
    title: "Name",
    question: () => "Schön, dass du da bist. Wie darf ich dich nennen?",
    render: (host, valid) => {
      host.appendChild(
        input("text", "Dein Vorname", ans.firstName, (v) => {
          ans.firstName = v.trim();
          valid(ans.firstName.length >= 1);
        }),
      );
    },
    isValid: () => ans.firstName.length >= 1,
    summary: () => ans.firstName,
  },
  {
    key: "anrede",
    // ⚠️ KEIN applyVoice mehr. Hier stand ein Muster auf `\bdu\b`; die
    //    Erkennung lieferte „Perdoo" und es griff nicht. Zwei Knöpfe
    //    beantwortet ein Finger fehlerfrei.
    eingabe: "wahl",
    title: "Anrede",
    question: () =>
      `${ans.firstName}, sollen wir per Du sein — oder lieber Sie?`,
    render: (host, valid) => {
      host.appendChild(
        choice(
          [
            { label: "Per Du", value: "Du" },
            { label: "Per Sie", value: "Sie" },
          ],
          () => ans.anrede,
          (v) => {
            ans.anrede = v as Answers["anrede"];
            valid(true);
          },
        ),
      );
    },
    isValid: () => ans.anrede !== "",
    summary: () => (ans.anrede === "Sie" ? "Per Sie" : "Per Du"),
  },
  {
    key: "geburt",
    /*
     * ⚠️ KORREKTUR an mir selbst (15.09.2026, spaeter Abend): Ich hatte das
     *    hier auf „feld" gesetzt mit der Begruendung „eine Geburtszeit
     *    diktiert niemand". Das war eine Annahme, und der Code widerlegt sie:
     *    applyVoice unten zerlegt einen GANZEN SATZ („ich bin am 21.08.1975
     *    um 14 Uhr in Wiener Neustadt geboren") in Datum, Uhrzeit und Ort,
     *    samt der Falle, dass „21.08.1975" nicht als 21:08 gelesen wird.
     *
     *    Martins Regel trennt AUSWAHL von TEXT — und das hier ist Text. Drei
     *    Felder auf einem Telefon auszufuellen ist genau das, was Sprache
     *    abnehmen kann. Die Felder bleiben als Rueckfall darunter stehen.
     */
    eingabe: "text",
    applyVoice: (t) => {
      const d = parseDate(t);
      if (d) ans.birthday = d;
      // Datum aus dem Text entfernen, damit parseTime es nicht als Uhrzeit
      // missdeutet (z.B. „21.08.1975" sonst als 21:08).
      const rest = t
        .replace(/\b\d{1,2}\s*[.,\-/ ]\s*\d{1,2}\s*[.,\-/ ]\s*\d{2,4}\b/, " ")
        .replace(
          /\b\d{1,2}\.?\s*(?:januar|februar|märz|maerz|april|mai|juni|juli|august|september|oktober|november|dezember)\s*\d{2,4}\b/i,
          " ",
        );
      const ti = parseTime(rest);
      if (ti) ans.birthTime = ti;
      // Ort: alles nach dem letzten „in " bis zum Satzende (mehrteilige Städte
      // wie „Wiener Neustadt"); ein angehängtes „… geboren" wird abgeschnitten.
      const pm = rest.match(/\bin\s+(.+?)(?:\s+geboren\b.*)?\s*[.?!]*\s*$/i);
      if (pm) {
        const place = pm[1].trim().replace(/\s+/g, " ");
        if (place.length >= 2 && place.length <= 60) ans.birthPlace = place;
        // 24.09.2026, Ticket 2567: „… in Deutschland geboren" setzt auch das
        // Land (das Land bestimmt die Zeitzone). Eine Stadt allein nicht —
        // aus einem Ort wird nicht geraten; das Feld darunter zeigt, was gilt.
        const land = geburtslandFinden(place.split(",").pop() ?? place);
        if (land) ans.birthCountry = land.schluessel;
      }
    },
    title: "Geburtsdaten",
    optional: true,
    question: () =>
      siez(
        "Wann und wo bist du geboren? Daraus lese ich dein Tages- und Jahresbild — und später, wenn du magst, mehr.",
        "Wann und wo sind Sie geboren? Daraus lese ich Ihr Tages- und Jahresbild — und später, wenn Sie mögen, mehr.",
      ),
    render: (host, valid) => {
      // ⚠️ Dieselbe Regel wie isValid() weiter unten, sonst bleibt „Weiter"
      //    grau, obwohl der Schritt gueltig ist. Genau so stand es hier: die
      //    Gueltigkeit war schon gelockert, diese Pruefung nicht — der Knopf
      //    blieb also weiter an der Geburtszeit haengen.
      const check = (): void => valid(ans.birthday.length === 10);
      const d = input("date", "Geburtsdatum", ans.birthday, (v) => {
        ans.birthday = v.trim();
        check();
      });
      const t = input("time", "Geburtszeit (wenn bekannt)", ans.birthTime, (v) => {
        ans.birthTime = v.trim();
        check();
      });
      const p = input("text", "Geburtsort, z.B. Wien", ans.birthPlace, (v) => {
        ans.birthPlace = v.trim();
      });
      // 24.09.2026, Ticket 2567: das Land bestimmt die Zeitzone des Charts.
      const land = baueGeburtslandAuswahl({
        wert: ans.birthCountry,
        klasse: "onb-input",
        ariaLabel: "Geburtsland",
      });
      land.addEventListener("change", () => {
        ans.birthCountry = land.value;
      });
      d.setAttribute("aria-label", "Geburtsdatum");
      t.setAttribute("aria-label", "Geburtszeit");
      p.setAttribute("aria-label", "Geburtsort");
      const row = document.createElement("div");
      row.className = "onb-row";
      row.append(d, t);
      host.append(row, p, land);
    },
    /*
     * ⚠️ 15.09.2026: Das war der EINZIGE Schritt ohne Ausweg, und er verlangte
     *    die Geburts-UHRZEIT. Die kennt längst nicht jeder. Wer sie nicht
     *    weiß, kam hier nicht weiter — der einzige Ausgang war das X, und das
     *    beendet den ganzen Willkommens-Lauf.
     *
     *    Jetzt reicht das Datum, und überspringen geht auch (optional).
     */
    isValid: () => ans.birthday.length === 10,
    summary: () =>
      `${ans.birthday}${ans.birthTime ? ", " + ans.birthTime : ""}${ans.birthPlace ? " · " + ans.birthPlace : ""}${ans.birthCountry ? " · " + geburtslandAnzeige(ans.birthCountry) : ""}`,
  },
  {
    key: "anliegen",
    eingabe: "text",
    applyVoice: (t) => {
      ans.anliegen = t.trim().replace(/\s+/g, " ");
    },
    title: "Anliegen",
    question: () =>
      siez(
        "Was möchtest du hier? Sag es in einem Satz — es muss nicht perfekt sein.",
        "Was möchten Sie hier? Sagen Sie es in einem Satz — es muss nicht perfekt sein.",
      ),
    render: (host, valid) => {
      const ta = document.createElement("textarea");
      ta.className = "onb-input onb-textarea";
      ta.placeholder =
        "z.B. meinen Alltag festhalten, zur Ruhe kommen, meine Lebensgeschichte bewahren …";
      ta.value = ans.anliegen;
      ta.addEventListener("input", () => {
        ans.anliegen = ta.value.trim();
        valid(ans.anliegen.length >= 2);
      });
      host.appendChild(ta);
    },
    isValid: () => ans.anliegen.length >= 2,
    summary: () => ans.anliegen,
  },
  {
    key: "beruf",
    imErstlauf: false,
    eingabe: "text",
    applyVoice: (t) => {
      ans.beruf = t.trim().replace(/\s+/g, " ");
    },
    title: "Alltag",
    question: () =>
      `Was ${du() === "Sie" ? "machen Sie" : "machst du"} beruflich, oder was füllt ${du() === "Sie" ? "Ihre" : "deine"} Tage?`,
    render: (host, valid) => {
      const inp = document.createElement("input");
      inp.type = "text";
      inp.className = "onb-input";
      inp.placeholder = "z.B. Trainer, Vater, im Ruhestand, Künstlerin …";
      inp.value = ans.beruf;
      inp.addEventListener("input", () => {
        ans.beruf = inp.value.trim();
        valid(true);
      });
      host.appendChild(inp);
    },
    isValid: () => true,
    optional: true,
    summary: () => ans.beruf || "übersprungen",
  },
  {
    key: "uebermich",
    imErstlauf: false,
    eingabe: "text",
    applyVoice: (t) => {
      ans.uebermich = t.trim().replace(/\s+/g, " ");
    },
    title: "Zur Person",
    question: () =>
      `Gibt es etwas, das ich über ${du() === "Sie" ? "Sie" : "dich"} wissen sollte — etwas, das ${du() === "Sie" ? "Ihnen" : "dir"} wichtig ist?`,
    render: (host, valid) => {
      const ta = document.createElement("textarea");
      ta.className = "onb-input onb-textarea";
      ta.placeholder = "z.B. eine Leidenschaft, ein wichtiger Mensch, ein Wert …";
      ta.value = ans.uebermich;
      ta.addEventListener("input", () => {
        ans.uebermich = ta.value.trim();
        valid(true);
      });
      host.appendChild(ta);
    },
    isValid: () => true,
    optional: true,
    summary: () => ans.uebermich || "übersprungen",
  },
  {
    key: "abend",
    imErstlauf: false,
    // Eine Uhrzeit laesst sich gut sagen („halb zehn") — applyVoice unten
    // kann das. Keine Auswahl, also spricht hier auch nichts dagegen.
    eingabe: "text",
    applyVoice: (t) => {
      const ti = parseTime(t);
      if (ti) ans.abendZeit = ti;
    },
    title: "Abend-Reflexion",
    question: () =>
      `Wann passt ${du() === "Sie" ? "Ihnen" : "dir"} abends eine kurze Reflexion? Dann fasse ich ${du() === "Sie" ? "Ihren" : "deinen"} Tag für ${du() === "Sie" ? "Sie" : "dich"} zusammen.`,
    render: (host, valid) => {
      host.appendChild(
        input("time", "z.B. 21:00", ans.abendZeit, (v) => {
          ans.abendZeit = v.trim();
          valid(true);
        }),
      );
    },
    isValid: () => true,
    optional: true,
    summary: () => ans.abendZeit || "später entscheiden",
  },
  {
    key: "anonym",
    imErstlauf: false,
    eingabe: "wahl",
    // ⚠️ Hier stand ein Sprach-Zerleger. Seit dieser Schritt als Auswahl
    //    gilt, konnte er nie mehr laufen — toter Code, entstanden durch
    //    meine eigene Aenderung von heute. Zwei Knoepfe beantwortet der
    //    Finger; wer das rueckgaengig macht, braucht ihn ohnehin neu.
    title: "Andere Menschen",
    question: () =>
      `Wenn andere Menschen in ${du() === "Sie" ? "Ihren" : "deinen"} Erzählungen vorkommen — sollen sie immer anonym bleiben, oder mit Vornamen (nur für ${du() === "Sie" ? "Sie" : "dich"} sichtbar)?`,
    render: (host, valid) => {
      host.appendChild(
        choice(
          [
            { label: "Immer anonym", value: "anonym" },
            { label: "Mit Vornamen", value: "vorname" },
          ],
          () => ans.anonym,
          (v) => {
            ans.anonym = v as Answers["anonym"];
            valid(true);
          },
        ),
      );
    },
    isValid: () => ans.anonym !== "",
    optional: true,
    summary: () =>
      ans.anonym === "vorname" ? "mit Vornamen (privat)" : "immer anonym",
  },
  /*
   * ⚠️ 2026-09-03 — Umgezogen, nicht neu erfunden.
   *
   * Diese fuenf Fragen standen bis heute als Knoepfe auf der Zuhause-Seite.
   * Gemessen an Martins Verlauf wurden sie in vier Monaten 13 MAL getippt
   * (0,15 % von 8774 Nachrichten), fast alle in einer Woche im August — das
   * war Ausprobieren, kein Benutzen. Fuer ihn also Moebel.
   *
   * Fuer einen NEUEN Nutzer waren sie die einzige Stelle in der ganzen App,
   * die zeigt, was man ueberhaupt sagen kann. Deshalb stehen sie jetzt hier,
   * am Ende des ersten Gespraechs — dort, wo genau diese Frage aufkommt.
   *
   * ⚠️ Sie kommen aus ZUHAUSE_WEGE, nicht abgeschrieben: eine Quelle, damit
   * die Liste nicht an zwei Stellen auseinanderlaeuft.
   *
   * Ein Schritt ohne Antwort: nichts einzugeben, nichts zu pruefen. `optional`
   * und `isValid: true`, damit „Fertig" sofort bereit steht.
   */
  {
    key: "beispiele",
    // Nur zum Lesen — hier gibt es gar keine Antwort.
    eingabe: "feld",
    title: "Beispiele",
    question: () =>
      `Fast fertig${ans.firstName ? ", " + ans.firstName : ""}. Zum Schluss ein paar Beispiele — ` +
      siez("sag es einfach, wie du es denkst.", "sagen Sie es einfach, wie Sie es denken."),
    render: (host, valid) => {
      const liste = document.createElement("ul");
      liste.className = "onb-beispiele";
      for (const w of ZUHAUSE_WEGE) {
        const li = document.createElement("li");
        li.textContent = `„${w.frage}"`;
        liste.appendChild(li);
      }
      host.appendChild(liste);
      valid(true);
    },
    isValid: () => true,
    optional: true,
    summary: () => "",
  },
];

// --------------------------------------------------------------------------
// Speichern
// --------------------------------------------------------------------------
function buildProfileNotes(): string {
  const lines: string[] = [];
  if (ans.anrede) lines.push(`Anrede: ${ans.anrede}`);
  if (ans.anliegen) lines.push(`Anliegen: ${ans.anliegen}`);
  if (ans.beruf) lines.push(`Beruf/Alltag: ${ans.beruf}`);
  if (ans.uebermich) lines.push(`Wichtig für mich: ${ans.uebermich}`);
  if (ans.abendZeit) lines.push(`Abend-Reflexion: ${ans.abendZeit}`);
  if (ans.anonym)
    lines.push(
      `Andere Menschen: ${ans.anonym === "vorname" ? "mit Vornamen (privat)" : 'immer anonym ("dein Gegenüber")'}`,
    );
  return lines.join("\n");
}

async function saveProfile(): Promise<void> {
  const payload: Record<string, string> = {
    firstName: ans.firstName,
    profileNotes: buildProfileNotes(),
  };
  if (ans.birthday) payload.birthday = ans.birthday;
  if (ans.birthTime) payload.birthTime = ans.birthTime;
  if (ans.birthPlace) payload.birthPlace = ans.birthPlace;
  if (ans.birthCountry) payload.birthCountry = ans.birthCountry;
  try {
    await fetch("/api/user/profile", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });
  } catch {
    /* fail-quiet — der lokale Done-Marker verhindert eine Endlosschleife */
  }
}

/**
 * Das HD-Chart nach dem Willkommen vorwärmen. (24.09.2026, Ticket 2567)
 *
 * ⚠️ Vorher fragte das hier sofort /api/human-design. Für ein Frei-Konto
 *    ist das 402 — und der globale 402-Abfang (app.ts, showUpgradeSheet)
 *    öffnete das Verkaufsfenster direkt nach dem Willkommen. Jetzt zuerst
 *    die Kurzfassung (für alle offen, kein Sprachmodell, wärmt das Chart);
 *    die Deutung nur, wenn sie für dieses Konto freigeschaltet ist.
 */
export async function hdVorwaermen(): Promise<void> {
  try {
    const r = await fetch("/api/human-design/kurzfassung", {
      credentials: "same-origin",
    });
    const j = (await r.json().catch(() => null)) as { deutung?: boolean } | null;
    if (j?.deutung === true) {
      await fetch("/api/human-design", { credentials: "same-origin" });
    }
  } catch {
    /* Vorwärmen ist optional */
  }
}

// --------------------------------------------------------------------------
// Widget
/**
 * Die Schritte des ERSTEN Durchgangs.
 *
 * ⚠️ 15.09.2026, Martin: *„Das kann man einfach vereinfachen."* Vorher liefen
 *    alle neun. Vier davon waren schon immer überspringbar (Alltag, Zur
 *    Person, Abend-Reflexion, Andere Menschen) — sie stehen jetzt nicht mehr
 *    zwischen einem neuen Menschen und seinem ersten Satz. Gelöscht ist
 *    nichts: Vespera fragt diese Dinge im Gespräch, wo sie hingehören.
 *
 *    Übrig bleiben fünf: Name, Anrede, Geburtsdaten, Anliegen, Beispiele —
 *    davon sind nur die ersten beiden Pflicht.
 */
export function erstlaufSchritte(alle: Step[] = STEPS): Step[] {
  return alle.filter((x) => x.imErstlauf !== false);
}

// --------------------------------------------------------------------------
function openOnboarding(_profile: Profile = {}): void {
  if (document.querySelector(".onb-overlay")) return;

  // Bekanntes aus dem Profil vorbefüllen — nie nach etwas fragen, das schon
  // bekannt ist (z.B. wenn der Vorname/Geburtsdaten teils gesetzt sind).
  if (_profile.firstName?.trim()) ans.firstName = _profile.firstName.trim();
  if (_profile.birthday?.trim()) ans.birthday = _profile.birthday.trim();
  if (_profile.birthTime?.trim()) ans.birthTime = _profile.birthTime.trim();
  if (_profile.birthPlace?.trim()) ans.birthPlace = _profile.birthPlace.trim();
  if (_profile.birthCountry?.trim()) ans.birthCountry = _profile.birthCountry.trim();

  const overlay = document.createElement("div");
  overlay.className = "sheet sheet--modal page-sheet onb-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Willkommen");

  /** Die Schritte dieses Durchgangs — einmal bestimmt, dann fest. */
  const lauf = erstlaufSchritte();

  const card = document.createElement("div");
  card.className = "sheet-card onb-card";

  const close = (): void => {
    stopSpeaking();
    releaseMic();
    markDone();
    overlay.remove();
  };
  card.appendChild(buildSheetHeader("Willkommen", close));

  // Begrüßung
  const intro = document.createElement("div");
  intro.className = "onb-intro";
  intro.innerHTML =
    '<p class="onb-hi">Schön, dass du da bist.</p>' +
    '<p class="onb-sub">Such dir zuerst eine Stimme aus. Danach <b>sprichst du einfach</b> — wo es etwas zu wählen gibt, tippst du es an.</p>';
  card.appendChild(intro);

  // Fortschritt (Schritt-Liste)
  const list = document.createElement("div");
  list.className = "onb-steps";
  card.appendChild(list);

  let active = 0;
  let handsFree = false; // freihändig: Bär hört selbst zu (nach Stimmen-Wahl)
  const stepEls: HTMLElement[] = [];

  const finishAll = async (): Promise<void> => {
    await saveProfile();
    // Geburtsdaten vorhanden → HD-Chart im Hintergrund vorwärmen. So ist
    // beim ersten Öffnen sofort eine Erkenntnis da. Fire-and-forget.
    if (ans.birthday && ans.birthTime) void hdVorwaermen();
    markDone();
    releaseMic();
    // Letzter Schritt: erste Aufnahme als Aha-Moment (optional, überspringbar).
    // Sicher per textContent (firstName ist Nutzer-Eingabe → kein innerHTML).
    intro.replaceChildren();
    const hi = document.createElement("p");
    hi.className = "onb-hi";
    hi.textContent = `Danke, ${ans.firstName || "schön"}.`;
    const subP = document.createElement("p");
    subP.className = "onb-sub";
    subP.textContent =
      "Jetzt der schönste Teil: " +
      siez(
        "Erzähl mir in etwa 30 Sekunden, wie dein Tag heute war.",
        "Erzählen Sie mir in etwa 30 Sekunden, wie Ihr Tag heute war.",
      ) +
      ` Daraus entsteht ${dein()} erster Eintrag.`;
    intro.append(hi, subP);
    list.replaceChildren();

    /*
     * 24.09.2026 (Onboarding-Plan, Ticket 2567): Die Paketkarten „Begleiter
     * 14,99 €“ und „Lebensbuch 29,99 €“ sind weg. Ihre Stripe-Zahlungslinks
     * sind abgeschaltet (active=false) — ab 01.10. fehlten dort Pflicht-
     * angaben, und die Preislinie ist jetzt Frei → Probewoche 6 € → Spur.
     * Der neue Kaufweg kommt mit Stufe 4b des Plans, im Web, nicht hier.
     */

    const cta = document.createElement("div");
    cta.className = "onb-finish";
    const rec = document.createElement("button");
    rec.type = "button";
    rec.className = "btn btn--primary onb-rec";
    rec.textContent = "Jetzt 30 Sekunden erzählen";
    rec.addEventListener("click", () => {
      stopSpeaking();
      overlay.remove();
      void import("./alltag-view.js").then((m) =>
        m.openRecorderOverlay({ autoStopMs: 40000 }),
      );
    });
    const later = document.createElement("button");
    later.type = "button";
    later.className = "btn btn--ghost onb-later";
    later.textContent = "Später";
    later.addEventListener("click", close);
    cta.append(rec, later);
    list.appendChild(cta);

    // Prompt sprechen. FREIHÄNDIG: danach selbst die Aufnahme starten (Mikro
    // ist schon frei) — sonst redet der Nutzer ins Leere, weil „erzähl mir 30
    // Sekunden“ automatisch klingt. Der Button bleibt für den Tipp-Pfad.
    const storyPrompt =
      `Danke, ${ans.firstName}. Jetzt der schönste Teil: ` +
      siez(
        "erzähl mir in etwa dreißig Sekunden, wie dein Tag heute war.",
        "erzählen Sie mir in etwa dreißig Sekunden, wie Ihr Tag heute war.",
      );
    if (handsFree) {
      void (async () => {
        await speak(storyPrompt, true);
        stopSpeaking();
        overlay.remove();
        const m = await import("./alltag-view.js");
        await m.openRecorderOverlay({ autoStopMs: 40000 });
      })();
    } else {
      void speak(storyPrompt);
    }
  };

  const renderStep = (i: number): void => {
    const step = lauf[i];
    const el = document.createElement("div");
    el.className = "onb-step is-active";
    stepEls[i] = el;

    /*
     * Wie weit ist es noch?
     *
     * ⚠️ 15.09.2026: Der Weg ist jetzt kurz (fünf statt neun Fragen) — aber
     *    das sah man ihm nicht an. Die abgehakten Schritte darüber sagen, was
     *    war; diese Zeile sagt, was noch kommt. Genau die Auskunft, die einen
     *    Ablauf kurz WIRKEN lässt, wenn er kurz IST.
     */
    const zaehler = document.createElement("p");
    zaehler.className = "onb-zaehler";
    zaehler.textContent =
      i === lauf.length - 1
        ? "Letzte Frage"
        : `Frage ${i + 1} von ${lauf.length}`;
    el.appendChild(zaehler);

    const q = document.createElement("p");
    q.className = "onb-q";
    q.textContent = step.question();
    el.appendChild(q);

    const fields = document.createElement("div");
    fields.className = "onb-fields";
    el.appendChild(fields);

    const actions = document.createElement("div");
    actions.className = "onb-actions";
    const next = document.createElement("button");
    next.type = "button";
    next.className = "btn btn--primary onb-next";
    next.textContent = i === lauf.length - 1 ? "Fertig" : "Weiter";
    next.disabled = !step.isValid();

    let stepDone = false;
    let pendingAdvance: number | null = null;
    const go = (): void => {
      if (stepDone) return;
      stepDone = true;
      advance(i);
    };

    /*
     * ⚠️ 15.09.2026 (Martin): Eine Auswahl ist mit dem Finger schon
     *    beantwortet. Ein zweiter Tipp auf „Weiter" ist ein Schritt zu viel —
     *    genau die Umständlichkeit, die er gemeldet hat. Deshalb trägt ein
     *    „wahl"-Schritt gar keinen Weiter-Knopf: der Tipp IST die Antwort.
     *    Die kurze Frist lässt den gewählten Knopf noch aufleuchten.
     */
    const valid = (ok: boolean): void => {
      next.disabled = !ok;
      if (ok && step.eingabe === "wahl" && !stepDone) {
        pendingAdvance = window.setTimeout(() => {
          pendingAdvance = null;
          go();
        }, 280);
      }
    };
    if (step.eingabe === "wahl") next.hidden = true;
    step.render(fields, valid);

    // Freihändig: nach der Stimmen-Wahl hört der Bär selbst zu — kein Tippen,
    // kein Klicken. Er stellt die Frage, wartet auf Stille, trägt ein,
    // bestätigt und macht weiter. Tippen bleibt als Rückfall (Feld + „Weiter“).
    if (step.eingabe === "text" && step.applyVoice && handsFree) {
      const vc = document.createElement("div");
      vc.className = "onb-voice onb-voice--hands";
      const pulse = document.createElement("div");
      pulse.className = "onb-listen-pulse";
      const status = document.createElement("div");
      status.className = "onb-voice-status";
      status.textContent = "Einen Moment …";
      const typeLink = document.createElement("button");
      typeLink.type = "button";
      typeLink.className = "onb-type-link";
      typeLink.textContent = "Lieber tippen";
      typeLink.addEventListener("click", () => {
        // ⚠️ Die Angabe gehoert IN den Aufruf, nicht als Zusicherung dahinter:
        //    `eslint --fix` im Tor haelt ein nachgestelltes `as HTMLElement`
        //    fuer ueberfluessig und entfernt es — dann steht hier `Element`,
        //    und `focus()` gibt es dort nicht. Genau das ist heute passiert.
        const inp = fields.querySelector<HTMLElement>("input, textarea");
        inp?.focus();
        status.textContent = "Tipp deine Antwort und dann „Weiter“.";
      });
      vc.append(pulse, status, typeLink);
      el.appendChild(vc);

      void (async () => {
        await speak(step.question(), true);
        for (let attempt = 0; attempt < 3 && !stepDone; attempt++) {
          status.textContent = "Ich höre dir zu …";
          pulse.classList.add("is-listening");
          const text = await listenVAD();
          pulse.classList.remove("is-listening");
          if (stepDone) return;
          if (!text) {
            if (attempt < 2) {
              await speak("Ich hab nichts gehört. Sag es nochmal.", true);
              continue;
            }
            status.textContent =
              "Ich hör dich gerade nicht — du kannst auch tippen.";
            return;
          }
          status.textContent = "Verstanden: „" + text + "“";
          step.applyVoice?.(text);
          fields.innerHTML = "";
          step.render(fields, valid);
          next.disabled = !step.isValid();
          if (step.isValid()) {
            await speak(confirmText(step.key), true);
            if (!stepDone) go();
            return;
          }
          if (attempt < 2)
            await speak("Das hab ich nicht ganz. Sag es nochmal.", true);
        }
      })();
    }

    /*
     * Ein „wahl"-Schritt im freihändigen Lauf: der Bär stellt die Frage laut —
     * das gehört zum Gespräch — und wartet dann. Kein Zuhören, kein Nachfragen.
     * Die Zeile darunter sagt, was zu tun ist, damit niemand ins Leere wartet.
     */
    if (step.eingabe === "wahl" && handsFree) {
      const hinweis = document.createElement("div");
      hinweis.className = "onb-voice-status onb-tipp-hinweis";
      hinweis.textContent = "Tipp auf deine Antwort.";
      el.appendChild(hinweis);
      void speak(step.question(), true);
    }

    // Sprach-Antwort zum Antippen (Rückfall, wenn nicht freihändig): der Bär
    // stellt die Frage (TTS), du tippst das Mikro, sprichst, es geht weiter.
    if (step.eingabe === "text" && step.applyVoice && !handsFree) {
      const vc = document.createElement("div");
      vc.className = "onb-voice";
      const mic = document.createElement("button");
      mic.type = "button";
      mic.className = "onb-mic";
      // 16.09.2026: eigene Zeichnung mit Strichstaerke 2 — jetzt dasselbe
      // Mikrofon wie ueberall, aus dem gemeinsamen Vorrat.
      mic.innerHTML = zeichen("mikro", 19);
      const lbl = document.createElement("span");
      lbl.className = "onb-mic-lbl";
      lbl.textContent = "Mit Stimme antworten";
      mic.appendChild(lbl);
      const status = document.createElement("div");
      status.className = "onb-voice-status";
      let rec: { stop: () => Promise<string> } | null = null;
      mic.addEventListener("click", async () => {
        if (mic.classList.contains("is-work")) return;
        if (!rec) {
          if (pendingAdvance !== null) {
            window.clearTimeout(pendingAdvance);
            pendingAdvance = null;
          }
          stopSpeaking();
          try {
            rec = await startRecording();
            mic.classList.add("is-rec");
            lbl.textContent = "Ich höre … (zum Stoppen tippen)";
            status.textContent = "";
          } catch {
            status.textContent =
              "Mikrofon nicht verfügbar — du kannst auch tippen.";
          }
          return;
        }
        const r = rec;
        rec = null;
        mic.classList.remove("is-rec");
        mic.classList.add("is-work");
        lbl.textContent = "Einen Moment …";
        const text = await r.stop();
        mic.classList.remove("is-work");
        lbl.textContent = "Mit Stimme antworten";
        if (!text) {
          status.textContent =
            "Ich hab nichts verstanden — bitte nochmal, oder tippe.";
          return;
        }
        step.applyVoice?.(text);
        fields.innerHTML = "";
        step.render(fields, valid);
        next.disabled = !step.isValid();
        status.textContent = "Verstanden: „" + text + "“";
        if (step.isValid()) {
          // Bär bestätigt gesprochen, was er verstanden hat, und geht dann von
          // selbst weiter — bei den Geburtsdaten mit mehr Zeit zum Korrigieren.
          // Mikro erneut tippen bricht das Weitergehen ab (siehe oben).
          void speak(confirmText(step.key));
          pendingAdvance = window.setTimeout(
            () => {
              if (!stepDone) go();
            },
            step.key === "geburt" ? 3400 : 1900,
          );
        }
      });
      vc.append(mic, status);
      el.appendChild(vc);
    }

    actions.appendChild(next);
    /*
     * ⚠️ 15.09.2026, im vollen Durchlauf aufgefallen: Im LETZTEN Schritt
     *    standen „Fertig" und „Überspringen" nebeneinander — und taten
     *    dasselbe. `skip` ruft go(), und `next` ruft bei einem optionalen
     *    Schritt ebenfalls go(), ohne Prüfung. Zwei Knöpfe, ein Ausgang.
     *
     *    Martins Regel vom 27.08. trifft genau das: nie ein Knopf, der
     *    dorthin führt, wo man schon steht. Am Ende heißt „Fertig" bereits
     *    „weiter", also braucht es das Überspringen dort nicht.
     */
    if (step.optional && i < lauf.length - 1) {
      const skip = document.createElement("button");
      skip.type = "button";
      skip.className = "btn btn--ghost onb-skip";
      skip.textContent = "Überspringen";
      skip.addEventListener("click", () => go());
      actions.appendChild(skip);
    }
    el.appendChild(actions);
    list.appendChild(el);

    next.addEventListener("click", () => {
      if (!step.isValid() && !step.optional) return;
      go();
    });

    // Bär spricht die Frage (kleine Verzögerung, damit das Reveal sitzt).
    // Im Freihändig-Modus übernimmt das die Zuhör-Schleife oben.
    const sprichtSchon =
      handsFree && (step.eingabe === "wahl" || Boolean(step.applyVoice));
    if (!sprichtSchon) {
      window.setTimeout(() => void speak(step.question()), 260);
    }
  };

  const collapse = (i: number): void => {
    const step = lauf[i];
    const el = stepEls[i];
    if (!el) return;
    el.className = "onb-step is-done";
    el.replaceChildren();
    const head = document.createElement("div");
    head.className = "onb-done-head";
    head.innerHTML =
      '<span class="onb-check" aria-hidden="true">✓</span>' +
      `<span class="onb-done-title">${step.title}</span>`;
    const val = document.createElement("span");
    val.className = "onb-done-val";
    val.textContent = step.summary();
    head.appendChild(val);
    el.appendChild(head);
  };

  function advance(i: number): void {
    collapse(i);
    // Die Begrüßung hat ihre Aufgabe getan, sobald die erste Frage beantwortet
    // ist — ab hier tritt sie zurück, statt jede weitere Frage nach unten zu
    // schieben (siehe .onb-intro.is-kompakt).
    intro.classList.add("is-kompakt");
    void saveProfile();
    active = i + 1;
    if (active >= lauf.length) {
      void finishAll();
      return;
    }
    renderStep(active);
    // sanft zum neuen Schritt scrollen
    window.setTimeout(() => {
      stepEls[active]?.scrollIntoView({ behavior: sanft(), block: "center" });
    }, 80);
  }

  // Stimme zuerst wählen (Martins Idee): der Tipp auf eine Stimme schaltet auf
  // iOS Mikro + Audio frei → danach läuft die Befragung freihändig. Klappt das
  // Mikro nicht, fällt es sauber auf Antippen/Tippen zurück.
  /*
   * Die Stimmen, die zur Wahl stehen.
   *
   * ⚠️ 16.09.2026, Martin: *„Ich möchte die Stimme verwenden, die ich auch
   *    verwende, die schöne von Soniox, und nicht so eine komische
   *    Roboterstimme."*
   *
   *    Hier standen vier Google-Kennungen (`de-DE-Chirp3-HD-*`), nachgeschlagen
   *    in GOOGLE_NARRATOR_VOICES — einer Liste, die seit dem 29.06. nur noch
   *    ZWEI Einträge hat. Zwei der vier Karten fielen also still weg, und die
   *    verbleibenden zwei sprachen Google.
   *
   *    Nachgemessen im Protokoll: `[voice-preview] cloud-tts 998ms 111405b
   *    voice=de-DE-Chirp3-HD-Aoede` — Google antwortet wirklich und in einer
   *    Sekunde. Das ist die Stimme, die Martin gehört hat.
   *
   * ⚠️ DER PREIS, den er damit wählt: Soniox vertont ungefähr in Echtzeit.
   *    Gemessen am selben Tag: `[voice-preview] soniox 2401ms` und `4299ms`
   *    gegen Googles 998 ms. Der Bär setzt im Willkommens-Ablauf also
   *    spürbar später ein. Das ist der bewusste Tausch: seine Stimme gegen
   *    eine Sekunde.
   */
  const CURATED = [
    "soniox:Grace",
    "soniox:Daniel",
    "soniox:Iris",
    "soniox:Nathan",
  ];
  const startConversation = (): void => {
    stoppeProbe();
    list.replaceChildren();
    renderStep(0);
  };

  /* ═══ Vorhören ═══════════════════════════════════════════════════════
   *
   * Nach dem Vorbild des Wählers in den Einstellungen
   * (settings-view.ts, playReadVoicePreview): POST /api/voice-preview mit
   * dem Anbieter, der zur Kennung passt, dann den Ton abspielen.
   *
   * ⚠️ Immer nur EINE Probe. Wer schnell durch vier Stimmen tippt, soll
   *    nicht vier übereinander hören.
   */
  let probeAudio: HTMLAudioElement | null = null;
  let probeUrl: string | null = null;

  function stoppeProbe(): void {
    if (probeAudio) {
      try {
        probeAudio.pause();
        probeAudio.removeAttribute("src");
        probeAudio.load();
      } catch {
        /* schon weg */
      }
      probeAudio = null;
    }
    if (probeUrl) {
      try {
        URL.revokeObjectURL(probeUrl);
      } catch {
        /* egal */
      }
      probeUrl = null;
    }
  }

  async function spieleProbe(id: string, karte: HTMLElement): Promise<void> {
    stoppeProbe();
    voiceSel
      .querySelectorAll(".onb-voice-card")
      .forEach((x: Element) => x.removeAttribute("data-probe"));
    karte.dataset.probe = "laedt";
    try {
      const r = await fetch("/api/voice-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          voiceId: id,
          ttsProvider: id.startsWith("soniox:") ? "soniox" : "elevenlabs",
          text: "Hallo, ich bin da. So klinge ich, wenn ich dir zuhöre und vorlese.",
        }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      probeUrl = URL.createObjectURL(blob);
      const a = new Audio(probeUrl);
      probeAudio = a;
      karte.dataset.probe = "spielt";
      const fertig = (): void => {
        if (karte.dataset.probe) delete karte.dataset.probe;
        stoppeProbe();
      };
      a.addEventListener("ended", fertig);
      a.addEventListener("error", fertig);
      await a.play();
    } catch {
      // Lieber ehrlich stumm bleiben als so tun, als liefe etwas.
      karte.dataset.probe = "still";
      window.setTimeout(() => {
        if (karte.dataset.probe === "still") delete karte.dataset.probe;
      }, 2600);
    }
  }
  const voiceSel = document.createElement("div");
  voiceSel.className = "onb-voices";
  for (const id of CURATED) {
    const v = VOICE_OPTIONS.find((x) => x.id === id);
    if (!v) continue;
    const c = document.createElement("button");
    c.type = "button";
    c.className = "onb-voice-card";
    const nm = document.createElement("div");
    nm.className = "onb-voice-name";
    // 15.09.2026: hier standen ♀ und ♂ als Unicode — auf iOS bunte Emoji.
    // Martin hat sie am 24.07. schon einmal abgeschafft; im Willkommens-
    // Schritt waren sie geblieben. Jetzt dasselbe gezeichnete Zeichen wie
    // in den Einstellungen, aus dem gemeinsamen Vorrat.
    const g = document.createElement("span");
    g.className = "voice-card-gender voice-card-gender--" + v.gender;
    g.setAttribute("aria-label", v.gender === "female" ? "weiblich" : "männlich");
    g.innerHTML = zeichen(v.gender === "female" ? "weiblich" : "maennlich", 14);
    const nmLbl = document.createElement("span");
    nmLbl.textContent = v.label;
    nm.append(g, nmLbl);
    const ds = document.createElement("div");
    ds.className = "onb-voice-desc";
    ds.textContent = v.description ?? "";
    c.append(nm, ds);
    /*
     * ⚠️ 16.09.2026, Martin: *„Das Stimmen beim Onboarding ist nett, aber ohne
     *    sie anzuhören haben sie keinen Zweck. Das ist doch logisch. Also
     *    natürlich braucht es ein Vorhören, damit ich entscheiden kann."*
     *
     *    Er hat recht: bis hierher startete ein Tipp auf eine Karte SOFORT das
     *    Gespräch. Man wählte also blind und war festgelegt, bevor man einen
     *    Ton gehört hatte.
     *
     *    Jetzt trennt sich beides: der Tipp wählt und spielt eine Probe, der
     *    Knopf darunter startet. Das Mikrofon wird in DESSEN Tipp
     *    freigeschaltet — auf iOS braucht das eine echte Geste, und der
     *    Start-Tipp ist eine.
     *
     * ⚠️ Die Probe braucht spürbar Zeit (Soniox, gemessen 2,4 bis 4,3 s).
     *    Deshalb sagt die Karte, dass sie lädt — vier Sekunden Stille nach
     *    einem Tipp fühlen sich kaputt an.
     */
    c.addEventListener("click", () => {
      voiceSel
        .querySelectorAll(".onb-voice-card")
        .forEach((x: Element) => x.classList.remove("is-picked"));
      c.classList.add("is-picked");
      try {
        // ⚠️ `readVoiceChosen` gehört dazu — ohne das Flag gilt die Wahl dem
        //    Server nicht als getroffen und er kann auf `realtimeVoice`
        //    zurückfallen. Beide anderen Stimm-Wähler setzen es ebenfalls
        //    (settings-view.ts und app.ts); hier fehlte es als Einziges.
        saveSettings({ ...loadSettings(), voiceId: id, readVoiceChosen: true });
      } catch {
        /* egal */
      }
      void spieleProbe(id, c);
    });
    voiceSel.appendChild(c);
  }
  /*
   * Der Start. Bis zum 16.09. war das der Tipp auf eine Stimm-Karte — man war
   * festgelegt, bevor man einen Ton gehört hatte. Jetzt wählt die Karte, und
   * dieser Knopf beginnt.
   *
   * ⚠️ Das Mikrofon wird HIER freigeschaltet. Auf iOS geht das nur in einer
   *    echten Geste, und dieser Tipp ist eine.
   */
  const losGehts = document.createElement("button");
  losGehts.type = "button";
  losGehts.className = "btn btn--primary onb-los";
  losGehts.textContent = "Los geht's";
  losGehts.addEventListener("click", async () => {
    stoppeProbe();
    losGehts.disabled = true;
    losGehts.textContent = "Einen Moment …";
    handsFree = await acquireMic();
    startConversation();
  });

  const typeInstead = document.createElement("button");
  typeInstead.type = "button";
  typeInstead.className = "btn btn--ghost onb-type-instead";
  typeInstead.textContent = "Lieber tippen";
  typeInstead.addEventListener("click", () => {
    handsFree = false;
    startConversation();
  });
  list.append(voiceSel, losGehts, typeInstead);

  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
